import React, { useState, useCallback } from "react";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { Label } from "@src/common/components";
import AddressInput from "@src/orders/create/components/AddressInput";
import StopInputs from "./StopInputs";
import { FieldErrors, FieldValues } from "react-hook-form";
import {
  AddressBookAutoFillType,
  AddressBookEntry,
  EndCustomer,
  Maybe,
  Shipment,
  ShipmentStopType,
  TimeWindowSetting,
} from "@api/graphql/generated/generated-types";
import { MapPinIcon as MapPinIconSolid } from "@heroicons/react/24/solid";
import {
  camelCaseToHumanized,
  formatTitleCaseWithUnderscores,
} from "@src/common/lib/textUtils";
import { appendUniqueEmailRecipients } from "@src/common/lib/emailUtils";
import { useAuth } from "@src/common/hooks/useAuth";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import {
  CustomLocationModal,
  CustomLocationResult,
} from "@src/common/components/CustomLocationModal";
import { useGooglePlaceLocation } from "@src/orders/create/hooks/useGooglePlaceLocation";

type Props = {
  errors: FieldErrors<FieldValues>;
  value: any;
  selectedCustomer: EndCustomer | undefined;
  index: number;
  key: number | string;
  getValues: any;
  setValue: any;
  clearErrors: any;
  setError: any;
  control: any;
  stopPathName?: string;
  isRecurring?: boolean;
  selectedDuration?: string;
  onStopDateChange?: () => void;
  selectedShipment: Shipment | undefined;
  isSuiteRequired?: boolean;
  isPhoneNumberRequired?: boolean;
  isCompanyNameRequired?: boolean;
};

function StopForm({
  errors,
  value,
  selectedCustomer,
  index,
  key,
  getValues,
  setValue,
  clearErrors,
  setError,
  control,
  stopPathName = "stops",
  isRecurring = false,
  selectedDuration,
  onStopDateChange,
  selectedShipment,
  isSuiteRequired = false,
  isPhoneNumberRequired = false,
  isCompanyNameRequired = false,
}: Props) {
  const { courier } = useAuth();
  const { showToast } = useCourieStore();
  const { getLocationFromPlaceId } = useGooglePlaceLocation();
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const stopType: ShipmentStopType = getValues(
    `${stopPathName}[${index}].type`
  );

  const showPickupCloseFields =
    courier?.courier?.pickupCloseTimesSetting ===
      TimeWindowSetting.BothPickUpAndDropOffDueTime ||
    courier?.courier?.pickupCloseTimesSetting ===
      TimeWindowSetting.AllTimeWindowSettings;

  const showDropoffReadyFields =
    courier?.courier?.pickupCloseTimesSetting ===
      TimeWindowSetting.BothDropOffReadyAndDueTime ||
    courier?.courier?.pickupCloseTimesSetting ===
      TimeWindowSetting.AllTimeWindowSettings;

  const handleAddressChange = (
    address: google.maps.places.AutocompletePrediction | undefined,
    index: number = 0
  ) => {
    if (address) {
      setValue(`${stopPathName}[${index}].streetAddress`, address.description);
      setValue(`${stopPathName}[${index}].googlePlaceId`, address.place_id);
      clearErrors(`${stopPathName}[${index}].streetAddress`);
      clearErrors(`${stopPathName}[${index}].googlePlaceId`);

      getLocationFromPlaceId(address.place_id).then((result) => {
        if (result?.location?.location) {
          const loc = result.location.location;
          const fullAddress = [
            loc.streetAddress,
            loc.city,
            loc.state,
            loc.country,
            loc.zipCode,
          ]
            .filter(Boolean)
            .join(", ");
          setValue(`${stopPathName}[${index}].streetAddress`, fullAddress);
          setValue(`${stopPathName}[${index}].locationId`, loc.id);
          setValue(`${stopPathName}[${index}].lngLat`, result.lngLat);
        } else {
          setValue(`${stopPathName}[${index}].locationId`, undefined);
        }
      }).catch((err) => {
        setValue(`${stopPathName}[${index}].locationId`, undefined);
        showErrorToast(err, showToast);
      });
    } else {
      setValue(`${stopPathName}[${index}].streetAddress`, undefined);
      setValue(`${stopPathName}[${index}].googlePlaceId`, undefined);
      setValue(`${stopPathName}[${index}].locationId`, undefined);
      setError(`${stopPathName}[${index}].streetAddress`, {
        type: "manual",
        message: "Please enter a valid address",
      });
    }
  };

  const handleAddressBookEntrySelected = (entry: AddressBookEntry) => {
    const setValueIfNotSet = (key: string, value?: Maybe<string>) => {
      if (value && !getValues(`${stopPathName}[${index}].${key}`)) {
        setValue(`${stopPathName}[${index}].${key}`, value);
      }
    };

    let fullAddress = `${entry.location.streetAddress}, ${entry.location.city}, ${entry.location.state}, ${entry.location.country} ${entry.location.zipCode}`;
    setValue(`${stopPathName}[${index}].streetAddress`, fullAddress);
    setValue(
      `${stopPathName}[${index}].googlePlaceId`,
      entry.location.googlePlaceId
    );
    setValue(`${stopPathName}[${index}].locationId`, entry.location.id);

    setValueIfNotSet("companyName", entry.companyName);
    setValueIfNotSet("contactName", entry.contactName);
    setValueIfNotSet("phone", entry.phone);
    setValueIfNotSet("suite", entry.suite);
    if (courier?.courier?.addressBookAutoFillType !== AddressBookAutoFillType.ExcludeInstructions) {
      setValueIfNotSet("instruction", entry.instruction);
    }

    const currentEmailRecipients: string =
      getValues("additionalEmailRecipients") || "";
    const updatedEmailRecipients = appendUniqueEmailRecipients(
      currentEmailRecipients,
      entry.emailRecipients as string[] | undefined
    );
    if (updatedEmailRecipients !== currentEmailRecipients) {
      setValue("additionalEmailRecipients", updatedEmailRecipients);
    }

    clearErrors(`${stopPathName}[${index}].streetAddress`);
    clearErrors(`${stopPathName}[${index}].googlePlaceId`);
  };

  const handleLocationSuccess = useCallback(
    (result: CustomLocationResult) => {
      const fullAddress = [
        result.streetAddress,
        result.city,
        result.state,
        result.country,
        result.zipCode,
      ]
        .filter(Boolean)
        .join(", ");
      setValue(`${stopPathName}[${index}].streetAddress`, fullAddress);
      setValue(`${stopPathName}[${index}].locationId`, result.locationId);
      setValue(`${stopPathName}[${index}].googlePlaceId`, "");
      setValue(`${stopPathName}[${index}].lngLat`, result.lngLat);
      clearErrors(`${stopPathName}[${index}].streetAddress`);
      clearErrors(`${stopPathName}[${index}].googlePlaceId`);
      setIsLocationModalOpen(false);
    },
    [setValue, clearErrors, stopPathName, index]
  );

  return (
    <li key={key} className="mb-10 ml-6 grid gap-2">
      {stopType === ShipmentStopType.PickUp ? (
        <span className="absolute flex items-center justify-center w-8 h-8 bg-primary-200 rounded-full -left-4 ring-4 ring-white dark:ring-gray-900 dark:bg-primary-900">
          <MapPinIcon className="w-5 h-5 text-primary-500" />
        </span>
      ) : (
        <span className="absolute flex items-center justify-center w-8 h-8 bg-primary-200 rounded-full -left-4 ring-4 ring-white dark:ring-gray-900 dark:bg-primary-900">
          <MapPinIconSolid className="w-5 h-5 text-red-500" />
        </span>
      )}
      <div className="col-span-3">
        <div className="flex justify-between items-center">
          <Label className="block text-xs font-medium text-gray-700 truncate">
            {formatTitleCaseWithUnderscores(stopType)} address
            {errors?.[`${stopPathName}`]?.[index]?.googlePlaceId && (
              <span className=" text-red-500">
                {`Please enter a ${camelCaseToHumanized(stopType)} address`}
              </span>
            )}
          </Label>
          <span
            className="text-xs truncate hover:underline hover:cursor-pointer text-blue-500 dark:text-blue-400"
            onClick={() => setIsLocationModalOpen(true)}
          >
            Set Custom Location
          </span>
        </div>
        <React.Fragment key={index}>
          <AddressInput
            required={true}
            handleAddressChange={(address) =>
              handleAddressChange(address, index)
            }
            control={control}
            size="mini"
            name={`${stopPathName}[${index}].streetAddress`}
            value={value}
            shouldReverseAddressSearch={true}
            handleAddressBookEntrySelected={handleAddressBookEntrySelected}
            endCustomerId={selectedCustomer?.id}
          />
        </React.Fragment>
      </div>

      <React.Fragment key={index}>
        <StopInputs
          stopIndex={index}
          selectedCustomer={selectedCustomer}
          stopPathName={stopPathName}
          isRecurring={isRecurring}
          selectedDuration={selectedDuration}
          onStopDateChange={onStopDateChange}
          selectedShipment={selectedShipment}
          showPickupCloseFields={showPickupCloseFields}
          showDropoffReadyFields={showDropoffReadyFields}
          isSuiteRequired={isSuiteRequired}
          isPhoneNumberRequired={isPhoneNumberRequired}
          isCompanyNameRequired={isCompanyNameRequired}
        />
      </React.Fragment>
      <CustomLocationModal
        isModalOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        onSuccess={handleLocationSuccess}
      />
    </li>
  );
}

export default StopForm;
